import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip
} from '@mui/material';
import axios from 'axios';

/**
 * Fetches and displays full lifecycle for one serialized item (by id).
 * Supports both ASSEMBLY (fittings only) and COMPONENT (fittings + services + inspections).
 */
const SerializedItemLifecycleDetail = ({ serializedItemId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!serializedItemId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`/api/serialized-items/${serializedItemId}/lifecycle`)
      .then((res) => setData(res.data))
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load lifecycle');
      })
      .finally(() => setLoading(false));
  }, [serializedItemId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!data) return null;

  const {
    entityType,
    fittings = [],
    services = [],
    inspections = [],
    removalHistory = [],
    adsbExecutions = [],
    ampExecutions = []
  } = data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold">
        Installation History (Fittings)
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {entityType === 'ASSEMBLY' ? (
                <>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Assembly Type</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Position</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Assembly</TableCell>
                  <TableCell>Code</TableCell>
                </>
              )}
              <TableCell>Installed</TableCell>
              <TableCell>Removed</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fittings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={entityType === 'ASSEMBLY' ? 7 : 6}>No installation history</TableCell>
              </TableRow>
            ) : (
              fittings.map((f, idx) => (
                <TableRow key={idx}>
                  <TableCell>{f.aircraftRegistration || '-'}</TableCell>
                  {entityType === 'ASSEMBLY' ? (
                    <>
                      <TableCell>{f.assemblyTypeName || '-'}</TableCell>
                      <TableCell>{f.serialNumber || '-'}</TableCell>
                      <TableCell>{f.position || '-'}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{f.assemblyTypeName || '-'}</TableCell>
                      <TableCell>{f.code || '-'}</TableCell>
                    </>
                  )}
                  <TableCell>{f.installedAt ? new Date(f.installedAt).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{f.removedAt ? new Date(f.removedAt).toLocaleDateString() : 'Current'}</TableCell>
                  <TableCell>{f.remarks || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight="bold">
        Removal History
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Removed</TableCell>
              <TableCell>Aircraft</TableCell>
              {entityType === 'ASSEMBLY' ? (
                <>
                  <TableCell>Assembly Type</TableCell>
                  <TableCell>Position</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Assembly</TableCell>
                  <TableCell>Code</TableCell>
                </>
              )}
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {removalHistory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={entityType === 'ASSEMBLY' ? 5 : 5}>No removal history</TableCell>
              </TableRow>
            ) : (
              removalHistory.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell>{r.removedAt ? new Date(r.removedAt).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{r.aircraftRegistration || '-'}</TableCell>
                  {entityType === 'ASSEMBLY' ? (
                    <>
                      <TableCell>{r.assemblyTypeName || '-'}</TableCell>
                      <TableCell>{r.position || '-'}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{r.assemblyTypeName || '-'}</TableCell>
                      <TableCell>{r.code || '-'}</TableCell>
                    </>
                  )}
                  <TableCell>{r.remarks || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight="bold">
        AD/SB Service Bulletin Execution History
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>WO #</TableCell>
              <TableCell>AD/SB #</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>WO Date</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adsbExecutions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No AD/SB execution history for this serial number</TableCell>
              </TableRow>
            ) : (
              adsbExecutions.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{e.workOrderNumber || '-'}</TableCell>
                  <TableCell>{e.bulletinNumber || '-'}</TableCell>
                  <TableCell>{e.bulletinTitle || e.title || '-'}</TableCell>
                  <TableCell>{e.aircraftRegistration || '-'}</TableCell>
                  <TableCell>{e.woDate ? new Date(e.woDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{e.completedDate ? new Date(e.completedDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell><Chip label={e.status} size="small" /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight="bold">
        (AMP) Schedule Execution History
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>WO #</TableCell>
              <TableCell>AMP #</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>WO Date</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ampExecutions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No AMP schedule execution history for this serial number</TableCell>
              </TableRow>
            ) : (
              ampExecutions.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{e.workOrderNumber || '-'}</TableCell>
                  <TableCell>{e.scheduleNumber || '-'}</TableCell>
                  <TableCell>{e.scheduleTitle || e.title || '-'}</TableCell>
                  <TableCell>{e.aircraftRegistration || '-'}</TableCell>
                  <TableCell>{e.woDate ? new Date(e.woDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{e.completedDate ? new Date(e.completedDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell><Chip label={e.status} size="small" /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {entityType === 'COMPONENT' && (
        <>
          <Typography variant="subtitle1" fontWeight="bold">
            Services (Overhaul, Repair)
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Completed By</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>No service records</TableCell>
                  </TableRow>
                ) : (
                  services.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.type || '-'}</TableCell>
                      <TableCell>{s.description || '-'}</TableCell>
                      <TableCell>{s.date ? new Date(s.date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{s.completedBy || '-'}</TableCell>
                      <TableCell><Chip label={s.status} size="small" /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" fontWeight="bold">
            Inspections
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Inspector</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inspections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>No inspection records</TableCell>
                  </TableRow>
                ) : (
                  inspections.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell>{i.type || '-'}</TableCell>
                      <TableCell>{i.description || '-'}</TableCell>
                      <TableCell>{i.date ? new Date(i.date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell><Chip label={i.result} size="small" color={i.result === 'PASS' ? 'success' : i.result === 'FAIL' ? 'error' : 'default'} /></TableCell>
                      <TableCell>{i.inspectorName || i.completedBy || '-'}</TableCell>
                      <TableCell><Chip label={i.status} size="small" /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default SerializedItemLifecycleDetail;
